export class ApiClientError extends Error {
    status: number
    code?: string

    constructor(message: string, status: number, code?: string) {
        super(message)
        this.name = 'ApiClientError'
        this.status = status
        this.code = code
    }
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    body?: unknown
    signal?: AbortSignal
}

/**
 * JSON fetch wrapper for the app's own API. Every request carries the
 * security header the API requires for state-changing calls.
 */
export const api = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    let response: Response

    try {
        response = await fetch(path, {
            method: options.method ?? 'GET',
            headers: {
                'Content-Type': 'application/json',
                'x-webreplica': '1'
            },
            body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
            credentials: 'same-origin',
            signal: options.signal
        })
    }
    catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') throw error

        throw new ApiClientError('Network error. Check your connection and try again.', 0)
    }

    const data = await response.json().catch(() => null) as { error?: string, code?: string } | null

    if (!response.ok) {
        throw new ApiClientError(data?.error ?? `Request failed with status ${response.status}.`, response.status, data?.code)
    }

    return data as T
}

export const errorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
    if (error instanceof ApiClientError) return error.message
    if (error instanceof Error && error.message) return error.message

    return fallback
}
